/* Domovoi · the couple's hub (logged-in homepage)

   Only runs when the <head> gate decided there's a stored session.
   Loads the Supabase SDK on demand (the logged-out page never pays
   for it), lets it refresh a lapsed token, then makes ONE call:
   household_overview(today) — a security-invoker function, so RLS
   decides what comes back: this couple's rows and nothing else.

   If the session turns out to be dead, it quietly hands the page
   back to the promo. */
import { API_URL, PUBLISHABLE_KEY, AUTH_STORAGE_KEY } from '../core/config.js';

(function () {
  'use strict';
  var root = document.documentElement;
  if (root.getAttribute('data-home') !== 'hub') return;

  var URL_ = API_URL;
  var KEY = PUBLISHABLE_KEY;
  var SDK = 'https://cdn.jsdelivr.net/npm/@supabase/supabase-js@2.117.2/dist/umd/supabase.js';
  var SDK_SRI = 'sha384-Rj26LVGvoeRVR6+mwQmFfcR3QOBEwT+ZmuCWpuiqeTzJpCs0ER4ITAWGb4Hiy3Ok';   // pinned: this runs with your session
  var POSTER = 'https://image.tmdb.org/t/p/w185';
  var hub = document.getElementById('hub');
  var client = null, lastLoad = 0;

  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function arrow() { return '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>'; }
  function poster(p) { return p ? POSTER + p : ''; }
  function plural(n, one, many) { return n === 1 ? one : (many || one + 's'); }
  function localISO(d) { d = d || new Date(); return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'); }
  function parseISO(s) { var p = s.split('-'); return new Date(+p[0], +p[1] - 1, +p[2]); }
  function ago(iso) {
    var t = new Date(iso), s = (Date.now() - t.getTime()) / 1000;
    if (s < 90) return 'just now';
    if (s < 3570) return Math.round(s / 60) + 'm ago';
    if (s < 6 * 3600) return Math.round(s / 3600) + 'h ago';
    var today = new Date(); today.setHours(0, 0, 0, 0);
    var day = new Date(t); day.setHours(0, 0, 0, 0);
    var dd = Math.round((today - day) / 86400000);
    if (dd === 0) return 'today, ' + t.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    if (dd === 1) return 'yesterday';
    if (dd < 7) return t.toLocaleDateString([], { weekday: 'long' });
    return t.toLocaleDateString([], { day: 'numeric', month: 'short' });
  }
  function pretty(id) { var s = String(id || '').replace(/[_-]+/g, ' ').trim(); return s.charAt(0).toUpperCase() + s.slice(1); }

  function backToPromo() {
    root.removeAttribute('data-home');
    hub.setAttribute('aria-busy', 'false');
    // The signed-out page is the redesign's (src/home-web/main.js turns it back on)
    window.dispatchEvent(new CustomEvent('dv-home-promo'));
  }

  function loadSDK() {
    return new Promise(function (res, rej) {
      if (window.supabase && window.supabase.createClient) return res();
      var s = document.createElement('script'); s.src = SDK; s.integrity = SDK_SRI; s.crossOrigin = 'anonymous'; s.onload = res; s.onerror = rej;
      document.head.appendChild(s);
    });
  }

  function start() {
    loadSDK().then(function () {
      client = window.supabase.createClient(URL_, KEY, { auth: { storageKey: AUTH_STORAGE_KEY } });
      return client.auth.getSession();
    }).then(function (r) {
      var session = r && r.data && r.data.session;
      if (!session) { backToPromo(); return; }
      // The token may have just been refreshed: the nav decided "signed out"
      // from the stale one, so let it look again.
      if (window.DVAccountMenu) window.DVAccountMenu.mount();
      return load();
    }).catch(function () {
      hub.innerHTML = '<div class="p-wrap h-error">We couldn\'t reach your home just now. <a href="/">Try again</a></div>';
      hub.setAttribute('aria-busy', 'false');
    });
  }

  function load() {
    lastLoad = Date.now();
    // Gifts come from their own function; if it fails the rest of the hub still draws.
    var gifts = client.rpc('gift_upcoming', { p_today: localISO(), p_days: 120 })
      .then(function (g) { return g.error ? null : g.data; }, function () { return null; });
    return Promise.all([client.rpc('household_overview', { p_today: localISO() }), gifts]).then(function (res) {
      var r = res[0];
      if (r.error) throw r.error;
      if (!r.data) { backToPromo(); return; }
      var d = r.data;
      d.gifts = res[1];
      if (window.DVIdentity) window.DVIdentity.save({ name: d.me && d.me.name, partner: d.partner ? d.partner.name : null, checkedAt: Date.now() });
      render(d);
    });
  }

  // Coming back to the tab after a while: refresh quietly.
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden && client && Date.now() - lastLoad > 60000) load().catch(function () {});
  });

  /* ------------------------------------------------------------ */

  function render(d) {
    var me = (d.me && d.me.name) || 'you';
    var P = d.partner && d.partner.name;
    var C = d.catered || {}, V = d.curated || {};
    var today = d.today, todayDate = parseISO(today);
    var week = C.week || [];
    var dinners = {};
    week.forEach(function (m) { if (!dinners[m.date] || m.slot === 'dinner') dinners[m.date] = m; });
    var planned = Object.keys(dinners).length;

    var h = new Date().getHours();
    var hello = h < 5 ? 'Still up' : h < 12 ? 'Morning' : h < 18 ? 'Afternoon' : 'Evening';

    var html = '<div class="p-wrap">';

    /* ---- header ---- */
    html += '<header class="h-head"><div>' +
      '<div class="p-eyebrow"><span class="dot"></span>' + (P ? 'Your home' : 'Your home, for now') + '</div>' +
      '<h1 class="h-greet">' + hello + ', <em>' + esc(me) + '.</em></h1>' +
      '<p class="h-sub">' + (P ? 'You and ' + esc(P) + '. Here\'s today.' : 'Here\'s today.') + '</p>' +
      '</div>' +
      '</header>';

    /* ---- needs you + tonight ---- */
    var tasks = needs(d, P, planned);
    html += '<div class="h-top">';
    html += '<section class="h-card"><div class="h-card-head"><h2 class="h-card-title">Needs you' + (tasks.length ? ' <span class="h-count">' + tasks.length + '</span>' : '') + '</h2></div>';
    if (tasks.length) {
      html += '<ul class="h-tasks">' + tasks.map(function (t) {
        return '<li><a class="h-task ' + t.k + '" href="' + t.href + '"><span class="bar"></span><span><span class="tt">' + t.title + '</span><span class="ts">' + t.sub + '</span></span><span class="go">' + t.cta + arrow() + '</span></a></li>';
      }).join('') + '</ul>';
    } else {
      html += '<div class="h-clear"><img src="assets/mascot-idle-160.png" alt=""><span><b>Nothing needs you.</b>The week\'s planned and the list is in hand.</span></div>';
    }
    html += '</section>';

    html += '<section class="h-card h-tonight"><div class="h-card-head"><h2 class="h-card-title">Tonight</h2><span class="h-link" style="color:rgba(252,251,246,.6)">' + todayDate.toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'long' }) + '</span></div>';
    var tonight = dinners[today];
    html += '<div class="h-tn">' + (tonight && tonight.image_url ? '<img class="art" src="' + esc(tonight.image_url) + '" alt="">' : '<span class="art">' + icon('fork') + '</span>') +
      '<div><div class="k">Dinner</div>' + (tonight
        ? '<div class="v">' + esc(tonight.title) + '</div><div class="s"><a href="/catered/">Open the recipe</a></div>'
        : '<div class="v">Nothing planned</div><div class="s">' + (C.recipes ? C.recipes + ' ' + plural(C.recipes, 'recipe') + ' saved. <a href="/catered/">Pick one</a>' : '<a href="/catered/">Save your first recipe</a>') + '</div>') +
      '</div></div>';
    var nw = (V.now_watching || [])[0], un = (V.up_next || [])[0];
    var tv = nw || un;
    html += '<div class="h-tn">' + (tv && tv.poster ? '<img class="art poster" src="' + poster(tv.poster) + '" alt="">' : '<span class="art">' + icon('tv') + '</span>') +
      '<div><div class="k">' + (nw ? 'Watching' : un ? 'Up next' : 'Telly') + '</div>' + (tv
        ? '<div class="v">' + esc(tv.title) + '</div><div class="s">' + (nw && nw.season ? 'Season ' + nw.season + (nw.of ? ' of ' + nw.of : '') + ' · ' : '') + '<a href="/curated/">Open Curated</a></div>'
        : V.to_watch ? '<div class="v">' + V.to_watch + ' on your list</div><div class="s"><a href="/curated/">Pick for us</a></div>'
        : '<div class="v">Nothing matched yet</div><div class="s"><a href="/curated/">Start swiping</a></div>') +
      '</div></div>';
    html += '</section></div>';

    /* ---- the three ---- */
    html += '<div class="h-products">';

    // Catered: today and the next three days (Olya: "why show all the prior days?")
    var ahead = {};
    (C.next_days || week).forEach(function (m) { if (!ahead[m.date] || m.slot === 'dinner') ahead[m.date] = m; });
    var nAhead = 0;
    for (var j = 0; j < 4; j++) { var dj = new Date(todayDate); dj.setDate(todayDate.getDate() + j); if (ahead[localISO(dj)]) nAhead++; }
    html += '<section class="h-card h-prod catered"><div class="h-card-head"><h2 class="h-card-title">Dinners</h2><span class="h-tag">Catered</span></div><div class="h-week h-week-4' + (nAhead ? '' : ' empty') + '">';
    for (var i = 0; i < 4; i++) {
      var day = new Date(todayDate); day.setDate(todayDate.getDate() + i);
      var iso = localISO(day), m = ahead[iso];
      var cls = 'h-day' + (iso === today ? ' today' : '') + (m ? ' has' : '');
      html += '<div class="' + cls + '"><span class="dn"><span>' + day.toLocaleDateString([], { weekday: 'short' }) + '</span><span>' + day.getDate() + '</span></span>' +
        (m ? (m.image_url ? '<img src="' + esc(m.image_url) + '" alt="">' : '') + '<span class="mt">' + esc(m.title) + '</span>'
           : (iso >= today ? '<a class="plus" href="/catered/" aria-label="Plan ' + day.toLocaleDateString([], { weekday: 'long' }) + '">+</a>' : '')) +
        '</div>';
    }
    html += '</div><div class="h-foot"><span>' + nAhead + ' of 4 nights · ' + (C.recipes || 0) + ' ' + plural(C.recipes || 0, 'recipe') + ' saved' + (C.ticked ? ' · ' + C.ticked + ' ticked off' : '') + '</span><a class="h-link" href="/catered/">Open Catered</a></div></section>';

    // Curated: what's on
    var rowItems = [], rowLabel = '';
    if ((V.now_watching || []).length || (V.up_next || []).length) {
      rowLabel = 'Watching and up next';
      (V.now_watching || []).forEach(function (x) { rowItems.push({ p: x.poster, t: x.title, pos: x.season ? 'S' + x.season : 'Watching' }); });
      (V.up_next || []).forEach(function (x, n) { rowItems.push({ p: x.poster, t: x.title, pos: '#' + (n + 1) }); });
    } else if ((V.recent_matches || []).length) {
      rowLabel = 'Latest matches';
      V.recent_matches.forEach(function (x) { rowItems.push({ p: x.poster, t: x.title }); });
    }
    html += '<section class="h-card h-prod curated"><div class="h-card-head"><h2 class="h-card-title">Your shows</h2><span class="h-tag">Curated</span></div>' +
      '<div class="h-stat"><div><b>' + (V.to_watch || 0) + '</b>to watch</div>' + (P ? '<div><b>' + (V.waiting_on_me || 0) + '</b>' + esc(P) + '\'s yeses for you</div>' : '') + '</div>';
    if (rowItems.length) {
      html += '<p class="h-sublabel">' + rowLabel + '</p><div class="h-posters">' + rowItems.slice(0, 5).map(function (x) {
        return '<figure>' + (x.p ? '<img src="' + poster(x.p) + '" alt="">' : '') + '<figcaption>' + (x.pos ? '<span class="pos">' + esc(x.pos) + '</span> ' : '') + esc(x.t) + '</figcaption></figure>';
      }).join('') + '</div>';
    } else {
      html += '<p class="h-quiet">Swipe a few and your matches show up here.</p>';
    }
    html += '<div class="h-foot"><span>' + (P && V.waiting_on_partner ? esc(P) + ' has ' + V.waiting_on_partner + ' of your yeses to get through' : '') + '</span><a class="h-link" href="/curated/">Open Curated</a></div></section>';

    // Considered: the next few occasions and where the gifts stand
    html += giftsCard(d, P);

    html += '</div>';

    /* ---- activity ---- */
    var acts = (d.activity || []).map(function (a) { return activityLine(a, me, P); }).filter(Boolean);
    html += '<section class="h-card"><div class="h-card-head"><h2 class="h-card-title">Lately</h2><span class="h-link" style="color:var(--fg-3)">The last three weeks</span></div>';
    html += acts.length ? '<ul class="h-acts">' + acts.join('') + '</ul>' : '<p class="h-quiet">Nothing yet. Whatever either of you does in Catered, Curated or Considered shows up here.</p>';
    html += '</section>';

    html += '</div>';
    hub.innerHTML = html;
    hub.setAttribute('aria-busy', 'false');
  }

  /* What needs you, most useful first. Each one goes straight to where it gets done. */
  function needs(d, P, planned) {
    var C = d.catered || {}, V = d.curated || {}, t = [];
    var today = d.today;
    var giftT = giftTasks(d, P);
    giftT.filter(function (g) { return g.days <= 7; }).forEach(function (g) { t.push(g); });
    if (P && V.waiting_on_me) {
      var stack = (V.waiting_posters || []).slice(0, 3).map(function (p) { return '<img src="' + poster(p) + '" alt="">'; }).join('');
      t.push({ k: 'curated', href: '/curated/', cta: 'Swipe them',
        title: (stack ? '<span class="stack">' + stack + '</span>' : '') + esc(P) + ' said yes to ' + V.waiting_on_me + ' ' + plural(V.waiting_on_me, 'title'),
        sub: 'They\'re at the front of your deck. Say yes too and they go on your list.' });
    }
    (V.new_seasons || []).slice(0, 2).forEach(function (s) {
      t.push({ k: 'curated', href: '/curated/', cta: 'See it', title: 'New season of ' + esc(s.title), sub: esc(s.note || 'It\'s back on your list.') });
    });
    var daysLeft = 7 - Math.round((parseISO(today) - parseISO(d.week_start)) / 86400000);
    if (planned === 0) {
      t.push({ k: 'catered', href: '/catered/', cta: 'Plan the week', title: 'Nothing\'s planned for dinner this week',
        sub: C.recipes ? 'You have ' + C.recipes + ' ' + plural(C.recipes, 'recipe') + ' saved. Drop a few onto the week and the shopping list builds itself.' : 'Paste a recipe link or two to get started.' });
    } else if (planned < 7) {
      var gaps = 0, mon = parseISO(d.week_start);
      for (var i = 0; i < 7; i++) { var day = new Date(mon); day.setDate(mon.getDate() + i); var iso = day.getFullYear() + '-' + String(day.getMonth() + 1).padStart(2, '0') + '-' + String(day.getDate()).padStart(2, '0'); if (iso >= today && !(d.catered.week || []).some(function (m) { return m.date === iso; })) gaps++; }
      if (gaps) t.push({ k: 'catered', href: '/catered/', cta: 'Fill the gaps', title: gaps + ' ' + plural(gaps, 'night') + ' left without a dinner', sub: planned + ' of 7 planned so far this week.' });
    }
    if (planned > 0 && !C.staples_reviewed) {
      t.push({ k: 'catered', href: '/catered/', cta: 'Check them', title: 'Check the cupboard staples before you shop', sub: 'Salt, oil and the like stay off the list until someone says you\'re running low.' });
    }
    if ((C.use_soon || []).length) {
      var names = C.use_soon.slice(0, 3).map(function (u) { return pretty(u.id); });
      t.push({ k: 'catered', href: '/catered/', cta: 'Find a recipe', title: 'Use soon: ' + esc(names.join(', ')), sub: 'In the kitchen and close to its date.' });
    }
    giftT.filter(function (g) { return g.days > 7; }).forEach(function (g) { t.push(g); });
    if (!P) {
      t.push({ k: 'home', href: '/account/', cta: 'Invite them', title: 'Bring your partner in', sub: 'Domovoi is built for two. Send them a code and they join this home.' });
    }
    if (V.to_watch && !(V.up_next || []).length && !(V.now_watching || []).length && t.length < 4) {
      t.push({ k: 'curated', href: '/curated/', cta: 'Choose', title: 'Pick your next five', sub: V.to_watch + ' ' + plural(V.to_watch, 'match', 'matches') + ' waiting. Put the ones you want first in Up next.' });
    }
    return t;
  }

  function activityLine(a, me, P) {
    var who = a.who === 'me' ? 'You' : a.who === 'partner' ? (P || 'Your partner') : 'You and ' + (P || 'your partner');
    var orb = a.who === 'both' ? '<span class="orb both">' + esc(me[0].toUpperCase()) + (P ? '+' + esc(P[0].toUpperCase()) : '') + '</span>'
      : '<span class="orb ' + (a.who === 'me' ? 'a' : 'b') + '">' + esc((a.who === 'me' ? me : (P || '?'))[0].toUpperCase()) + '</span>';
    var n = a.n || 1, title = a.title ? '<b>' + esc(a.title) + '</b>' : '', text;
    switch (a.kind) {
      case 'recipe':  text = who + ' saved ' + title; break;
      case 'planned': text = n > 1 ? who + ' planned ' + n + ' dinners, including ' + title : who + ' put ' + title + ' on the plan'; break;
      case 'ticked':  text = who + ' ticked off ' + n + ' ' + plural(n, 'thing') + ' on the shopping list'; break;
      case 'swiped':  text = who + ' said yes to ' + n + ' ' + plural(n, 'title'); break;
      case 'matched': text = n > 1 ? who + ' matched on ' + n + ' titles, latest ' + title : who + ' both want to watch ' + title; break;
      case 'watching': text = who + ' started ' + title + (n > 1 ? ' and ' + (n - 1) + ' more' : ''); break;
      case 'watched': text = n > 1 ? who + ' marked ' + n + ' as watched, including ' + title : who + ' finished ' + title; break;
      case 'person_added':    text = who + ' added ' + title + ' to your people'; break;
      case 'people_imported': text = who + ' imported ' + n + ' people from a calendar'; break;
      case 'person_updated':  text = who + ' updated ' + title; break;
      case 'gift_added':      text = n > 1 ? who + ' added ' + n + ' gift ideas, including ' + title : who + ' added a gift idea: ' + title; break;
      case 'gift_confirmed':  text = n > 1 ? who + ' confirmed ' + n + ' gifts, including ' + title : who + ' confirmed ' + title; break;
      case 'event_added':     text = who + ' put ' + title + ' on the calendar'; break;
      default: return '';
    }
    var img = '';
    if (a.img) img = a.product === 'curated' ? '<img src="' + poster(a.img) + '" alt="">' : '<img class="sq" src="' + esc(a.img) + '" alt="">';
    return '<li class="h-act">' + orb + '<span class="tx">' + text + '<span class="meta"><i class="' + a.product + '">' + ({ catered: 'Catered', curated: 'Curated', considered: 'Considered' }[a.product] || '') + '</i> · ' + ago(a.at) + '</span></span>' + (img || '<span></span>') + '</li>';
  }

  /* ---- gifts ---- */
  var FAMILY = /^(mum|mom|mother|mam|dad|father|nan|nana|nanny|gran|granny|grandma|grandmother|grandad|granddad|grandpa|grandfather|auntie|aunt|uncle|brother|sister|step-?mum|step-?mom|step-?dad|cousin|son|daughter)$/i;
  var OCC = { christmas: 'Christmas', valentines: 'Valentine\u2019s Day', 'mothers-day': 'Mother\u2019s Day', 'fathers-day': 'Father\u2019s Day',
              anniversary: 'Your anniversary', easter: 'Easter', hanukkah: 'Hanukkah', diwali: 'Diwali', 'eid-al-fitr': 'Eid al-Fitr', 'lunar-new-year': 'Lunar New Year' };
  function giftPerson(p, d, P) {
    if (p.member) return p.me ? 'You' : (P || p.name);
    var meId = d.me && d.me.id, name = String(p.name || '').trim();
    if (p.side && p.side !== meId && P && FAMILY.test(name)) return P + '\u2019s ' + name.toLowerCase();
    return name;
  }
  function giftTitle(o, d, P) {
    var ppl = o.people || [];
    if (o.key === 'birthday') { var l = giftPerson(ppl[0] || {}, d, P); return l === 'You' ? 'Your birthday' : l + (/s$/i.test(l) ? '\u2019' : '\u2019s') + ' birthday'; }
    if (o.key === 'mothers-day' && ppl.some(function (p) { return p.country === 'GB' || p.country === 'IE'; })) return 'Mothering Sunday';
    if (/^event:/.test(o.key)) return o.event_title || 'A date';
    return OCC[o.key] || o.key;
  }
  function giftHref(o) { return '/considered/occasion/' + encodeURIComponent(o.id) + '/' + o.date; }
  function inDays(n) { return n === 0 ? 'today' : n === 1 ? 'tomorrow' : 'in ' + n + ' days'; }
  function money(cc) { return { GB: '\u00a3', IE: '\u20ac' }[cc] || '$'; }

  // Occasions coming up with someone still to decide: three weeks out (five for Christmas)
  function giftTasks(d, P) {
    var G = (d.gifts && d.gifts.occasions) || [], out = [];
    var meId = d.gifts && d.gifts.me;
    G.forEach(function (o) {
      // Only people on your side, or both sides (gift_people.side): Olya isn't asked about Ben's uncle.
      // and only gifts: not someone you two said "a message", "nothing" or "not this year" for, or whose reminder you closed
      var ppl = (o.people || []).filter(function (p) { return !p.me && (!p.side || !meId || p.side === meId) && giftWanted(p); });
      var open = ppl.filter(function (p) { return !p.confirmed; });
      if (!open.length) return;
      var days = Math.round((parseISO(o.date) - parseISO(d.today)) / 86400000);
      if (days > (o.key === 'christmas' ? 35 : 21)) return;
      var title = giftTitle(o, d, P), sub;
      if (ppl.length === 1) {
        var p = ppl[0];
        sub = (p.ideas ? p.ideas + ' ' + plural(p.ideas, 'idea') + ', nothing confirmed yet.' : 'No ideas yet.') +
          (p.member ? ' ' + esc(P || 'They') + ' can\u2019t see this.' : '') +
          (p.budget_max ? ' Budget ' + money(d.gifts.country) + (p.budget_min != null ? Math.round(p.budget_min) + '\u2013' : 'up to ') + Math.round(p.budget_max) + '.' : '');
      } else {
        sub = open.length + ' of ' + ppl.length + ' people still to decide: ' + open.slice(0, 3).map(function (p) { return esc(giftPerson(p, d, P)); }).join(', ') + (open.length > 3 ? ' and more.' : '.');
      }
      out.push({ k: 'considered', href: giftHref(o), cta: open.some(function (p) { return p.ideas; }) ? 'Decide' : 'Find ideas', days: days,
        title: esc(title) + ' is ' + inDays(days), sub: sub });
    });
    return out;
  }

  function giftWanted(p) { return (!p.mode || p.mode === 'gift') && !p.skip && !p.closed; }
  function giftsCard(d, P) {
    var G = ((d.gifts && d.gifts.occasions) || []).map(function (o) {
      return Object.assign({}, o, { people: (o.people || []).filter(function (p) { return p.me || giftWanted(p); }) });
    }).filter(function (o) { return (o.people || []).some(function (p) { return !p.me; }); });
    var html = '<section class="h-card h-prod considered h-gifts"><div class="h-card-head"><h2 class="h-card-title">Gifts coming up</h2><span class="h-tag">Considered</span></div>';
    if (!d.gifts) {
      html += '<p class="h-quiet">Birthdays, Christmas and your anniversary, with who\u2019s getting what.</p>';
    } else if (!G.length) {
      html += '<p class="h-quiet">Nothing in the next four months yet. Add the people you buy for, or bring their birthdays in from your calendar.</p>' +
        '<div class="h-foot"><a class="p-btn coral sm" href="/considered/calendar/import">Import birthdays ' + arrow() + '</a><a class="h-link" href="/considered/calendar">Open the calendar</a></div></section>';
      return html;
    } else {
      html += '<ul class="h-gl">' + G.slice(0, 4).map(function (o) {
        var dt = parseISO(o.date), ppl = o.people.filter(function (p) { return !p.me; });
        var conf = ppl.filter(function (p) { return p.confirmed; }).length, ideas = ppl.filter(function (p) { return !p.confirmed && p.ideas; }).length;
        var pill = ppl.length === 1
          ? (conf ? '<span class="h-gp conf">Confirmed</span>' : ideas ? '<span class="h-gp idea">' + ppl[0].ideas + ' ' + plural(ppl[0].ideas, 'idea') + '</span>' : '<span class="h-gp none">Nothing yet</span>')
          : '<span class="h-gp ' + (conf === ppl.length ? 'conf' : conf || ideas ? 'idea' : 'none') + '">' + conf + ' of ' + ppl.length + ' done</span>';
        var lock = ppl.length && ppl.every(function (p) { return p.member; }) ? '<span class="h-glock" title="' + esc(P || 'Your partner') + ' can\u2019t see this">' +
          '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg></span>' : '';
        return '<li><a href="' + giftHref(o) + '"><span class="h-gd"><small>' + dt.toLocaleDateString([], { month: 'short' }) + '</small><b>' + dt.getDate() + '</b></span>' +
          '<span class="h-gt"><span>' + esc(giftTitle(o, d, P)) + lock + '</span><small>' + (function (n) { return n < 14 ? inDays(n).replace(/^in/, 'In') : 'In ' + Math.round(n / 7) + ' weeks'; })(Math.round((dt - parseISO(d.today)) / 86400000)) + '</small></span>' + pill + '</a></li>';
      }).join('') + '</ul>';
    }
    html += '<div class="h-foot"><a class="h-link" href="/considered/quiz">Find a gift</a><a class="h-link" href="/considered/calendar">Open the calendar</a></div></section>';
    return html;
  }

  function icon(k) {
    var p = k === 'tv'
      ? '<rect x="2" y="7" width="20" height="14" rx="2"/><path d="m17 2-5 5-5-5"/>'
      : '<path d="M3 2v7c0 1.1.9 2 2 2h0a2 2 0 0 0 2-2V2M5 2v20M16 2c-1.7 0-3 2-3 5s1.3 5 3 5m0 0v10m0-10c1.7 0 3-2 3-5s-1.3-5-3-5"/>';
    return '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' + p + '</svg>';
  }

  function go() { if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start(); }
  // Home redrawn (src/home-web/main.js) shows the week instead; the hub is its fallback, when the redesign turns off.
  if (root.hasAttribute('data-redesign')) {
    window.addEventListener('dv-redesign', function h(e) {
      if (e.detail && !e.detail.on) { window.removeEventListener('dv-redesign', h); go(); }
    });
  } else go();
})();
